import { useEffect, useState } from "react";

import {
  ApiError,
  createAsset,
  publishAsset,
  type Audience,
  type DocumentType,
} from "@/api/client";
import { AUDIENCE_LABEL, DOCUMENT_TYPE_SINGULAR } from "@/lib/asset-labels";
import { Button } from "@/components/ui/button";
import { FileInput } from "@/components/ui/file-input";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { FormError } from "@/components/ui/form-error";
import { usePopperAwareInteractOutside } from "@/lib/radix";
import { TaxonomyPicker } from "./TaxonomyPicker";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  token: string | undefined;
  onCreated: () => void;
}

const AUDIENCES: Audience[] = ["my_learning", "for_clients"];
const DOC_TYPES: DocumentType[] = ["one_pager", "battlecard", "slide_deck", "case_study"];

// Truthful, status-specific upload errors. The backend validates by content,
// not the client-declared MIME (CHP-355), so a real PDF is no longer rejected;
// when something genuinely fails we tell the user which thing, instead of the
// old catch-all that blamed the file type for every failure.
function uploadErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 415) return "Unsupported file type. Upload a PDF, PPTX, DOCX, or image.";
    if (err.status === 413) return "File too large. The maximum size is 25 MB.";
    if (err.status === 422) return "That file appears to be empty.";
    if (err.status === 409)
      return "An asset with this title already exists. Rename it, or edit the existing asset.";
  }
  return "Save failed. Please check your connection and try again.";
}

/** RM/RA "Add new resource" flow (CHP-273 Fix 5): a 65%-width right-hand
 * drawer. "Save as Draft" creates the asset as a DRAFT; "Publish" creates it
 * and immediately takes it live. If the publish step fails the draft survives
 * (recoverable — publish it from the manage panel). */
export function AddResourceDrawer({ open, onOpenChange, token, onCreated }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [heroImage, setHeroImage] = useState<File | null>(null);
  const [heroPreview, setHeroPreview] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [audience, setAudience] = useState<Audience>("for_clients");
  const [documentType, setDocumentType] = useState<DocumentType>("one_pager");
  const [topicIds, setTopicIds] = useState<string[]>([]);
  const [industryIds, setIndustryIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const onInteractOutside = usePopperAwareInteractOutside();

  // Live preview of the chosen hero image. createObjectURL avoids reading the
  // file into a base64 string; the cleanup revokes the URL on change/unmount
  // (idempotent under React 19 StrictMode's double-invoked effects).
  useEffect(() => {
    if (!heroImage) {
      setHeroPreview(null);
      return;
    }
    const url = URL.createObjectURL(heroImage);
    setHeroPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [heroImage]);

  // Close + reset, the single dismiss path. Radix does not re-invoke the Sheet's
  // onOpenChange when the controlled `open` prop is flipped externally, and this
  // drawer never unmounts (it's always rendered while isAdmin), so the form must
  // be cleared explicitly on every dismiss — Cancel included — or a half-filled
  // form (title/description/file/tags/error) leaks into the next open. Esc and
  // overlay-dismiss flow through the Sheet wrapper, which calls this too.
  function dismiss() {
    reset();
    onOpenChange(false);
  }

  function reset() {
    setFile(null);
    setHeroImage(null);
    setTitle("");
    setDescription("");
    setAudience("for_clients");
    setDocumentType("one_pager");
    setTopicIds([]);
    setIndustryIds([]);
    setError(null);
  }

  async function submit(publish: boolean) {
    // The Save/Publish buttons are disabled without a file; the title is
    // guarded here so both submit paths surface the same message.
    if (!token || !file) return;
    if (!title.trim()) {
      setError("A title is required.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const created = await createAsset(token, file, {
        title,
        description: description || undefined,
        audience,
        document_type: documentType,
        topic_ids: topicIds,
        industry_ids: industryIds,
        hero_image: heroImage ?? undefined,
      });
      if (publish) {
        try {
          await publishAsset(token, created.id);
        } catch {
          // The draft was created; only the publish step failed. Leave the
          // draft in place (recoverable) and surface a clear message.
          setError(
            "Saved as a draft, but publishing failed. Publish it from the manage panel when it's ready.",
          );
          onCreated();
          return;
        }
      }
      reset();
      onCreated();
      onOpenChange(false);
    } catch (err) {
      setError(uploadErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <SheetContent
        side="right"
        className="w-full overflow-y-auto sm:w-[65vw] sm:max-w-[65vw]"
        data-testid="add-resource-drawer"
        onInteractOutside={onInteractOutside}
      >
        <SheetHeader>
          <SheetTitle>Add new resource</SheetTitle>
          <SheetDescription>
            Save it as a draft, or publish to take it live immediately.
          </SheetDescription>
        </SheetHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit(false);
          }}
          className="mt-6 space-y-4"
        >
          <div className="space-y-1.5">
            <Label htmlFor="add-resource-file">File</Label>
            <FileInput
              id="add-resource-file"
              accept=".pdf,.pptx,.ppt,.docx,.doc,.png,.jpg,.jpeg"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="add-resource-hero">Hero image (optional)</Label>
            <FileInput
              id="add-resource-hero"
              accept="image/png,image/jpeg"
              onChange={(e) => setHeroImage(e.target.files?.[0] ?? null)}
            />
            {heroPreview ? (
              <img
                src={heroPreview}
                alt="Hero image preview"
                className="mt-2 h-24 w-24 rounded-md border border-border object-cover"
              />
            ) : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="add-resource-title">Title</Label>
            <Input
              id="add-resource-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="add-resource-description">Description (1-liner)</Label>
            <Input
              id="add-resource-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Audience</Label>
              <Select value={audience} onValueChange={(v) => setAudience(v as Audience)}>
                <SelectTrigger aria-label="Audience">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AUDIENCES.map((a) => (
                    <SelectItem key={a} value={a}>
                      {AUDIENCE_LABEL[a]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Document type</Label>
              <Select
                value={documentType}
                onValueChange={(v) => setDocumentType(v as DocumentType)}
              >
                <SelectTrigger aria-label="Document type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DOC_TYPES.map((d) => (
                    <SelectItem key={d} value={d}>
                      {DOCUMENT_TYPE_SINGULAR[d]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Topic</Label>
            <TaxonomyPicker kind="topic" token={token} selected={topicIds} onChange={setTopicIds} />
          </div>
          <div className="space-y-1.5">
            <Label>Industries</Label>
            <TaxonomyPicker
              kind="industry"
              token={token}
              selected={industryIds}
              onChange={setIndustryIds}
            />
          </div>
          {error ? <FormError>{error}</FormError> : null}
          <div className="flex flex-wrap justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={dismiss}>
              Cancel
            </Button>
            <Button type="submit" disabled={!file} loading={submitting} loadingLabel="Saving…">
              Save as draft
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={submitting || !file}
              onClick={() => void submit(true)}
            >
              Publish
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
